import IconChevronRight from 'assets/icons/chevron-right-stroke.svg';
import InfoIcon from 'assets/icons/info.svg';
import cn from 'classnames';
import PersonalizedRibbon from 'components/personalized-ribbon';
import Price from 'components/price';
import RatingBadge from 'components/rating-badge';
import Ribbon from 'components/ribbon';
import { useConfig } from 'config/use-config';
import { useCommon } from 'contexts/common';
import { useSegmentationContext } from 'contexts/segmentation';
import useIsClient from 'hooks/common/use-is-client';
import { CollectionProduct } from 'models/collection/collection';
import Image from 'next/image';
import Link from 'next/link';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useTrackingStore } from 'stores/tracking';
import { Gtm } from 'tracking';
import hasPersonalizedDiscount from 'utils/has-personalized-discount';
import { generalImageLoader } from 'utils/image-loader';
import { minusPercent } from 'utils/product';

interface PropTypes {
  backgroundColorClassName?: string;
  bestsellerClassname?: string;
  /** Collection Title for tracking */
  collectionTitle: string;
  foodaid?: boolean;
  iconClassName?: string;
  itemSize?: 'Normal' | 'Large' | undefined;
  marginClassName?: string;
  paddingClassName?: string;
  position: number;
  product: CollectionProduct;
  selected?: boolean;
  showSubscriptionPrice?: boolean;
  isSpecialLimitedSlider?: boolean;
  showWeights?: boolean;
  personalizedRibbonTextId?: string;
  subtitleClassname?: string;
  titleClassName?: string;
  onInfoClick?: () => void;
  onTileClick?: () => void;
  onToggle?: (productId: string) => void;
}

const ProductTile: React.FC<PropTypes> = ({
  backgroundColorClassName,
  bestsellerClassname,
  collectionTitle,
  foodaid = false,
  iconClassName,
  itemSize = 'Normal',
  marginClassName,
  paddingClassName,
  position,
  product,
  selected = false,
  isSpecialLimitedSlider = false,
  showSubscriptionPrice = false,
  showWeights = true,
  personalizedRibbonTextId,
  subtitleClassname,
  titleClassName,
  onInfoClick,
  onTileClick,
  onToggle,
}) => {
  // Use variantPrice directly from PDEX data when available
  const price = React.useMemo(() => {
    if (showSubscriptionPrice && product.variantPrice?.subscription?.price) {
      return parseFloat(product.variantPrice.subscription.price);
    }
    if (product.variantPrice?.one_off?.price) {
      return parseFloat(product.variantPrice.one_off.price);
    }
    // Fallback to first variant price
    const firstVariant = product.variants[0];
    return firstVariant ? parseFloat(firstVariant.priceV2.amount) : 0;
  }, [showSubscriptionPrice, product]);

  const comparePrice = React.useMemo(() => {
    if (showSubscriptionPrice && product.variantPrice?.subscription?.compare?.before) {
      return parseFloat(product.variantPrice.subscription.compare.before);
    }
    // For one-off, there's no compare price unless it's on sale
    return null;
  }, [showSubscriptionPrice, product]);

  const { globalDiscount } = useCommon();
  const { setTrackedList } = useTrackingStore();
  const { subscriptionDiscount } = useConfig();
  const hasDiscount = hasPersonalizedDiscount(product, globalDiscount, true);
  const subscriptionValue = hasDiscount
    ? globalDiscount.discount?.firstSubscriptionOrderDiscount
    : subscriptionDiscount;

  const { id: productId, title: productTitle, handle, availableForSale } = product;

  // Legacy calculation for products without variantPrice data
  const firstCheapestAvailableSubsVariant = React.useMemo(() => {
    // If we have variantPrice data, we don't need this calculation
    if (product.variantPrice) return null;

    // Legacy calculation for old data format
    if (showSubscriptionPrice) return null;

    const _firstCheapestAvailableSubsVariant = product.getFirstCheapestAvailableSubsVariant();
    return subscriptionValue && _firstCheapestAvailableSubsVariant
      ? minusPercent(+_firstCheapestAvailableSubsVariant.priceV2.amount, subscriptionValue)
      : null;
  }, [product, subscriptionValue, showSubscriptionPrice]);

  const onGoToProduct = React.useCallback(() => {
    const { tags, productType } = product;
    Gtm.track({
      group: Gtm.Group.Product,
      name: Gtm.ProductEvent.Click,
      data: {
        product: {
          productTitle,
          productId: productId,
          productType,
          tags: tags.join(','),
          position,
          list: collectionTitle || '',
        },
      },
    });
    setTrackedList(productId, collectionTitle || '');
  }, [collectionTitle, position, product, productId, productTitle, setTrackedList]);

  const promotedVariant = product.variants.find(
    (variant) => variant.id === product.promoteVariantId
  );

  const link =
    product.promoteVariantId && promotedVariant?.availableForSale
      ? `/products/${handle}?variant=${product.promoteVariantId}`
      : `/products/${handle}`;

  const displayTitle = React.useMemo(() => {
    return product.getProductTitle();
  }, [product]);

  const priceElement = React.useMemo(() => {
    return availableForSale ? (
      <div className="leading-tight">
        {comparePrice && (
          <span className={'text-grey-900 mr-1 text-xs line-through'}>
            <Price value={comparePrice} />
          </span>
        )}{' '}
        <span
          className={cn(comparePrice ? 'text-text-light-bg-discounted-price' : 'text-primary-main')}
        >
          {product.variants.length > 1 ? (
            <span data-tr-test="cdp:general:price_from">
              <FormattedMessage id="cdp:general:price_from" />
              &nbsp;
            </span>
          ) : null}

          <span className="notranslate">
            <Price value={firstCheapestAvailableSubsVariant ?? price} />
          </span>
        </span>
      </div>
    ) : (
      <span>
        <FormattedMessage id="cdp:productcard:back-soon" />
      </span>
    );
  }, [
    availableForSale,
    comparePrice,
    price,
    firstCheapestAvailableSubsVariant,
    product.variants.length,
  ]);

  const { isClient } = useIsClient();

  const { shouldOmitPersonalization, multipleSegmentation } = useSegmentationContext();

  const subTitle = useMemo(() => {
    if (shouldOmitPersonalization) {
      return product.getProductSubtitle();
    }
    return product.getProductSubtitle({
      segmentation: multipleSegmentation?.lastSegmentation,
    });
  }, [multipleSegmentation?.lastSegmentation, product, shouldOmitPersonalization]);

  const ribbon = useMemo(() => product.getRibbon(), [product]);

  const tile = React.useMemo(() => {
    return (
      <>
        <div
          className="absolute top-0 left-0 z-10 flex w-full flex-col items-start overflow-hidden rounded-tl-lg rounded-tr-lg text-center"
          data-test="ribbons"
        >
          {isClient && personalizedRibbonTextId && (
            <PersonalizedRibbon
              data-test="Personalized_Ribbon"
              ribbonTextId={personalizedRibbonTextId}
            />
          )}
          <div>
            <Ribbon
              data-test="Ribbon"
              title={ribbon.ribbonText}
              subTitle={ribbon.ribbonText2}
              color={ribbon.ribbonColor}
              product={product}
              className={cn('relative', !personalizedRibbonTextId && 'rounded-tl-lg')}
            />
          </div>
        </div>
        <div
          data-test="collection-tile-div1"
          data-id={product.id}
          className={cn(
            'relative flex w-full items-center rounded-lg border text-left shadow-sm md:h-full md:flex-col md:text-center md:hover:shadow-xl',
            bestsellerClassname,
            backgroundColorClassName ?? 'bg-backgrounds-other-white',
            paddingClassName || 'p-4',
            selected ? 'border-blue-text ring-blue-text ring-1' : 'border-grey-50',
            isSpecialLimitedSlider && 'text-center'
          )}
        >
          <div
            className={cn(
              itemSize === 'Normal' && 'w-full max-w-[6.5rem] min-w-[6.5rem] pt-6 lg:max-w-[10rem]',
              isSpecialLimitedSlider && 'max-w-[22rem] lg:max-w-[18rem]'
            )}
          >
            {product.image.src && (
              <Image
                data-test="image"
                loader={generalImageLoader}
                src={product.image.src}
                alt={product.image.alt || product.title || ''}
                width={800}
                height={800}
                sizes="(min-width: 1024px) 160px, (min-width: 769px) 104px, 40vw"
                className="mb-4 h-auto w-full"
              />
            )}
          </div>
          <div className={cn('flex w-full flex-1 flex-col pl-3 lg:pl-0', marginClassName)}>
            {/* Product Title */}
            <div
              data-test="display-product-title"
              className={cn(
                'text-blue-text mt-4 text-base leading-tight font-black lg:text-lg',
                titleClassName
              )}
            >
              {displayTitle}
            </div>

            {/* Product Rating */}
            <RatingBadge sku={product.id} iconSize={19} />

            {/* Product Subtitle */}
            {isClient && subTitle && (
              <div
                data-test="product.metadata.accentuate.subTitle"
                className={cn(
                  'text-grey-600 pt-1 text-sm leading-tight lg:pt-0',
                  subtitleClassname
                )}
              >
                {subTitle}
              </div>
            )}
            {/* Product Price */}
            <div
              data-test="product-price-priceElement"
              className="text-blue-text mt-auto mb-2 flex-none pt-2 font-black md:text-center"
            >
              {priceElement}
            </div>
            {/* Foodaid add to selection */}
            {foodaid && (
              <button
                data-test="foodaid-add-to-cart"
                onClick={() => onToggle && onToggle(product.variants[0].id)}
                className="focus-default bg-grey-200 text-primary-main mt-4 w-full rounded-lg py-4 text-sm font-black uppercase"
              >
                {selected ? (
                  <FormattedMessage id="foodaid:evaluation:item-added-to-cart" />
                ) : (
                  <FormattedMessage id="common:product:add" />
                )}
              </button>
            )}
          </div>

          {/* Variant Title Section */}
          {showWeights && !foodaid && (
            <div className="hidden w-full lg:block">
              <div className="bg-grey-50 -mx-4 -mb-4 flex flex-col items-center justify-center rounded-b py-1">
                <div
                  data-test="id=cdp:availableas"
                  className={cn(
                    'text-text-light-bg-secondary text-right text-[0.5rem] font-black tracking-wide uppercase'
                  )}
                >
                  <FormattedMessage id="cdp:availableas" />
                </div>
                <div className={'flex flex-row flex-wrap justify-center'}>
                  {product.getWeights().map((item, index) => (
                    <div
                      key={index}
                      className="border-grey-300 bg-other-white m-1 line-clamp-1 flex h-5 items-center rounded-lg border px-2 text-xs font-black"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {!foodaid && (
            <div className={cn('-mr-2 flex items-center md:hidden', iconClassName)}>
              <IconChevronRight className="text-c500 h-auto w-5" />
            </div>
          )}
        </div>
      </>
    );
  }, [
    backgroundColorClassName,
    bestsellerClassname,
    displayTitle,
    foodaid,
    iconClassName,
    isClient,
    isSpecialLimitedSlider,
    itemSize,
    marginClassName,
    onToggle,
    paddingClassName,
    personalizedRibbonTextId,
    priceElement,
    product,
    ribbon.ribbonColor,
    ribbon.ribbonText,
    ribbon.ribbonText2,
    selected,
    showWeights,
    subTitle,
    subtitleClassname,
    titleClassName,
  ]);

  return (
    <div className="relative flex h-full w-full">
      {foodaid ? (
        <div className="focus-default flex w-full rounded-sm">{tile}</div>
      ) : onTileClick ? (
        <button className="focus-default flex w-full rounded-sm" onClick={onTileClick}>
          {tile}
        </button>
      ) : (
        <Link
          href={link}
          // original classname default flex for mobile
          className={cn('focus-default flex w-full rounded-sm decoration-transparent')}
          onClick={onGoToProduct}
        >
          {tile}
        </Link>
      )}
      {onInfoClick && (
        <button
          onClick={onInfoClick}
          className="focus-default text-text-light-bg-secondary hover:text-grey-400 absolute bottom-0 left-0 mb-1 ml-1 flex h-8 w-8 items-center justify-center rounded-full md:top-0 md:right-0 md:bottom-auto md:left-auto md:mt-1 md:mr-1"
        >
          <InfoIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

export default ProductTile;
